import React, { useState } from 'react';
import { useToast } from './Toast';
import { getAdminToken } from './AdminGate';

/**
 * AdminWebhooks — Comprehensive webhook documentation & test tool for site administrators.
 */
export default function AdminWebhooks() {
  const toast = useToast();
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [testSlug, setTestSlug] = useState('killzone');
  const [testGameName, setTestGameName] = useState('US East Dedicated Room');
  const [testChannel, setTestChannel] = useState('US EAST');
  const [testMap, setTestMap] = useState('Beach Head');
  const [testPlayers, setTestPlayers] = useState('8/16');
  const [testScore, setTestScore] = useState('RED 4 - 2 BLUE');
  const [isSending, setIsSending] = useState(false);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://thecountryclub.lol';

  const copyToClipboard = (text, index, label) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success(`📋 Copied ${label} to clipboard!`);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const sampleJson = `{
  "gameName": "${testGameName}",
  "channel": "${testChannel}",
  "map": "${testMap}",
  "current": "${testPlayers}",
  "inGame": true,
  "players": ["ApexPredator", "Viper_Actual", "Shadow99", "NightOwl"],
  "playerStats": [
    { "name": "ApexPredator", "kills": 12, "deaths": 3, "score": 1450, "team": "RED" },
    { "name": "Viper_Actual", "kills": 9, "deaths": 4, "score": 1100, "team": "RED" }
  ],
  "teamScores": { "RED": 4, "BLUE": 2 },
  "score": "${testScore}"
}`;

  const curlCommand = `curl -X POST ${baseUrl}/webhook/games/${testSlug} \\
  -H "Content-Type: application/json" \\
  -d '${sampleJson.replace(/\n/g, ' ')}'`;

  const jsSnippet = `async function sendGameWebhook() {
  const response = await fetch('${baseUrl}/webhook/games/${testSlug}', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(${sampleJson})
  });
  return response.json();
}`;

  const sendTestWebhook = async () => {
    setIsSending(true);
    try {
      const res = await fetch(`/webhook/games/${testSlug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: sampleJson,
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`🚀 Test webhook dispatched successfully for "${testGameName}"!`);
      } else {
        toast.error(data.error || 'Failed to send test webhook');
      }
    } catch (err) {
      toast.error(`Error sending webhook: ${err.message}`);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Intro Box */}
      <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/15">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-emerald-400">📡</span>
          <h3 className="text-sm font-bold font-mono text-emerald-400">Webhook Integration Guide</h3>
        </div>
        <p className="text-xs text-emerald-400/60 font-mono leading-relaxed">
          External game server operators can push live match status to The Country Club via standard HTTP POST webhooks.
          Data is ingested instantly, mapped to the master catalog, and broadcast to online website visitors via Socket.IO.
        </p>
      </div>

      {/* Endpoints Quick Ref */}
      <div className="p-4 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-3">
        <h4 className="text-xs font-mono text-white/50 uppercase tracking-wider">Primary Endpoint</h4>
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-black/40 border border-white/[0.08] font-mono text-xs">
          <div className="flex items-center gap-2 min-w-0">
            <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-bold text-[10px]">POST</span>
            <code className="text-white/90 truncate">{baseUrl}/webhook/games/:gameSlug</code>
          </div>
          <button
            onClick={() => copyToClipboard(`${baseUrl}/webhook/games/killzone`, 0, 'Endpoint URL')}
            className="px-3 py-1.5 rounded bg-white/[0.06] hover:bg-white/[0.1] text-white/70 hover:text-white text-[11px] transition-colors cursor-pointer flex-shrink-0"
          >
            {copiedIndex === 0 ? '✓ Copied' : '📋 Copy URL'}
          </button>
        </div>
      </div>

      {/* Supported Payload Fields Table */}
      <div className="p-4 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-3">
        <h4 className="text-xs font-mono text-white/50 uppercase tracking-wider">Payload Specification</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono text-white/70">
            <thead>
              <tr className="border-b border-white/[0.08] text-white/30 uppercase text-[10px]">
                <th className="py-2 px-2">Field</th>
                <th className="py-2 px-2">Type</th>
                <th className="py-2 px-2">Status</th>
                <th className="py-2 px-2">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              <tr>
                <td className="py-2 px-2 text-emerald-400 font-bold">1) gameName</td>
                <td className="py-2 px-2 text-white/40">string</td>
                <td className="py-2 px-2 text-amber-400 font-bold">Required</td>
                <td className="py-2 px-2 text-white/60">Display title for room/lobby (e.g. "US EAST Dedicated Room")</td>
              </tr>
              <tr>
                <td className="py-2 px-2 text-emerald-400 font-bold">2) channel</td>
                <td className="py-2 px-2 text-white/40">string</td>
                <td className="py-2 px-2 text-amber-400 font-bold">Required</td>
                <td className="py-2 px-2 text-white/60">Server region or channel identifier (e.g. "US EAST")</td>
              </tr>
              <tr>
                <td className="py-2 px-2 text-emerald-400 font-bold">3) dnsAddress / dns</td>
                <td className="py-2 px-2 text-white/40">string</td>
                <td className="py-2 px-2 text-emerald-400/80">Optional</td>
                <td className="py-2 px-2 text-white/60">Server DNS IP address (Auto-resolved from 171-game catalog if omitted)</td>
              </tr>
              <tr>
                <td className="py-2 px-2 text-emerald-400 font-bold">4) current / players</td>
                <td className="py-2 px-2 text-white/40">string / array</td>
                <td className="py-2 px-2 text-emerald-400/80">Optional</td>
                <td className="py-2 px-2 text-white/60">Player count ratio ("8/16") & list of player names</td>
              </tr>
              <tr>
                <td className="py-2 px-2 text-emerald-400 font-bold">5) map / playlist</td>
                <td className="py-2 px-2 text-white/40">string / array</td>
                <td className="py-2 px-2 text-emerald-400/80">Optional</td>
                <td className="py-2 px-2 text-white/60">Current active map name (e.g. "Beach Head") or map rotation array</td>
              </tr>
              <tr>
                <td className="py-2 px-2 text-emerald-400 font-bold">6) playerStats</td>
                <td className="py-2 px-2 text-white/40">array</td>
                <td className="py-2 px-2 text-emerald-400/80">Optional</td>
                <td className="py-2 px-2 text-white/60">Detailed stats: <code className="text-white/40">[{`{ name, kills, deaths, score, team }`}]</code></td>
              </tr>
              <tr>
                <td className="py-2 px-2 text-emerald-400 font-bold">7) score / teamScores</td>
                <td className="py-2 px-2 text-white/40">string / object</td>
                <td className="py-2 px-2 text-emerald-400/80">Optional</td>
                <td className="py-2 px-2 text-white/60">Match score ("RED 4 - 2 BLUE") or object <code className="text-white/40">{`{ RED: 4, BLUE: 2 }`}</code></td>
              </tr>
              <tr>
                <td className="py-2 px-2 text-emerald-400 font-bold">8) round & timer telemetry</td>
                <td className="py-2 px-2 text-white/40">strings</td>
                <td className="py-2 px-2 text-emerald-400/80">Optional</td>
                <td className="py-2 px-2 text-white/60"><code className="text-white/40">currentRound</code>, <code className="text-white/40">totalRounds</code> (or <code className="text-white/40">roundCount: "3 of 11"</code>), <code className="text-white/40">timeRemaining</code></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Code Snippets (cURL & JS) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* cURL Example */}
        <div className="p-4 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-white/50 uppercase tracking-wider">cURL Command</span>
            <button
              onClick={() => copyToClipboard(curlCommand, 1, 'cURL command')}
              className="text-[11px] font-mono text-emerald-400 hover:underline cursor-pointer"
            >
              {copiedIndex === 1 ? '✓ Copied' : 'Copy cURL'}
            </button>
          </div>
          <pre className="p-3 rounded bg-black/60 border border-white/[0.05] text-[11px] font-mono text-emerald-300/90 overflow-x-auto whitespace-pre-wrap">
            {curlCommand}
          </pre>
        </div>

        {/* Node.js Example */}
        <div className="p-4 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-white/50 uppercase tracking-wider">Node.js Fetch Snippet</span>
            <button
              onClick={() => copyToClipboard(jsSnippet, 2, 'JavaScript code')}
              className="text-[11px] font-mono text-emerald-400 hover:underline cursor-pointer"
            >
              {copiedIndex === 2 ? '✓ Copied' : 'Copy JS'}
            </button>
          </div>
          <pre className="p-3 rounded bg-black/60 border border-white/[0.05] text-[11px] font-mono text-blue-300/90 overflow-x-auto whitespace-pre-wrap">
            {jsSnippet}
          </pre>
        </div>
      </div>

      {/* Interactive Webhook Test Tool */}
      <div className="p-5 rounded-xl bg-gradient-to-b from-white/[0.04] to-white/[0.02] border border-white/[0.08] space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-lg">🛠️</span>
          <h3 className="text-sm font-bold font-mono text-white">Live Webhook Dispatcher Tool</h3>
        </div>
        <p className="text-xs text-white/40 font-mono">
          Send a live test webhook payload directly to the server to verify active game card rendering and real-time Socket.IO updates.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs font-mono">
          <div>
            <label className="block text-[10px] text-white/40 uppercase mb-1">Game Slug</label>
            <input
              type="text"
              value={testSlug}
              onChange={(e) => setTestSlug(e.target.value)}
              className="w-full px-3 py-2 rounded bg-black/40 border border-white/[0.08] text-white focus:outline-none focus:border-emerald-500/40"
            />
          </div>
          <div>
            <label className="block text-[10px] text-white/40 uppercase mb-1">Room Name</label>
            <input
              type="text"
              value={testGameName}
              onChange={(e) => setTestGameName(e.target.value)}
              className="w-full px-3 py-2 rounded bg-black/40 border border-white/[0.08] text-white focus:outline-none focus:border-emerald-500/40"
            />
          </div>
          <div>
            <label className="block text-[10px] text-white/40 uppercase mb-1">Channel / Region</label>
            <input
              type="text"
              value={testChannel}
              onChange={(e) => setTestChannel(e.target.value)}
              className="w-full px-3 py-2 rounded bg-black/40 border border-white/[0.08] text-white focus:outline-none focus:border-emerald-500/40"
            />
          </div>
          <div>
            <label className="block text-[10px] text-white/40 uppercase mb-1">Active Map</label>
            <input
              type="text"
              value={testMap}
              onChange={(e) => setTestMap(e.target.value)}
              className="w-full px-3 py-2 rounded bg-black/40 border border-white/[0.08] text-white focus:outline-none focus:border-emerald-500/40"
            />
          </div>
          <div>
            <label className="block text-[10px] text-white/40 uppercase mb-1">Player Count</label>
            <input
              type="text"
              value={testPlayers}
              onChange={(e) => setTestPlayers(e.target.value)}
              className="w-full px-3 py-2 rounded bg-black/40 border border-white/[0.08] text-white focus:outline-none focus:border-emerald-500/40"
            />
          </div>
          <div>
            <label className="block text-[10px] text-white/40 uppercase mb-1">Match Score</label>
            <input
              type="text"
              value={testScore}
              onChange={(e) => setTestScore(e.target.value)}
              className="w-full px-3 py-2 rounded bg-black/40 border border-white/[0.08] text-white focus:outline-none focus:border-emerald-500/40"
            />
          </div>
        </div>

        <div className="pt-2">
          <button
            onClick={sendTestWebhook}
            disabled={isSending}
            className="px-5 py-2.5 rounded-lg bg-emerald-500/20 border border-emerald-500/40 hover:bg-emerald-500/30 text-emerald-300 text-xs font-mono font-bold transition-all cursor-pointer flex items-center gap-2"
          >
            <span>🚀</span>
            <span>{isSending ? 'Sending Webhook...' : 'Send Test Webhook Payload'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
